import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import { tracked } from '@glimmer/tracking';

import { Checkbox, Disclaimer, formatMoney, Spinner } from '@repo/design-system-kit';
import { dropTask } from 'ember-concurrency';

import { MainLayout } from 'register-partners-js/react/components/layouts/main';

import styles from './styles.module.css';

export default class RegistrationPaymentController extends Controller {
  mainLayout = MainLayout;

  spinner = Spinner;
  disclaimerBlock = Disclaimer.Block;

  @service checkout;
  @service payments;
  @service router;
  @service segment;
  @service currentUser;
  @service pricingService;
  @service intl;

  @tracked isComplete = false;
  @tracked stripeOptions = null;
  @tracked stripeCardElements = null;
  @tracked userName = null;
  @tracked termsAndConditionsChecked = false;

  checkbox = Checkbox;

  get selectedPricePlanRecurrence() {
    return this.model.selectedPricePlan.recurrence;
  }

  get capitalizedBrandName() {
    return this.model.estimation.product.brand_name;
  }

  get fieldsPending() {
    return !this.isComplete;
  }

  get promoOffer() {
    return this.model.estimation.product.promo_discount;
  }

  get paymentInvoiceAmount() {
    return this.model.estimation.total_amount.value;
  }

  get regularPrice() {
    return this.model.estimation.product.price_amount.value;
  }

  // oxlint-disable-next-line getter-return
  get disclaimerText() {
    if (this.promoOffer) {
      if (this.selectedPricePlanRecurrence === 'annual') {
        return this.intl.t('price_plan.company-creators.check-out.total-amount-promo', {
          regular_amount: formatMoney({
            amount: this.regularPrice,
            token: 'money-symbol-compact',
          }),
        });
      } else if (this.selectedPricePlanRecurrence === 'monthly') {
        return this.intl.t('order-new.disclaimer.monthly-promo', {
          discount_duration: this.promoOffer.month_duration,
          regular_amount: formatMoney({
            amount: this.regularPrice,
            token: 'money-symbol-compact',
          }),
          discounted_amount: formatMoney({
            amount: this.promoOffer.discounted_price.value,
            token: 'money-symbol-compact',
          }),
          name: this.capitalizedBrandName,
        });
      }
    }

    if (this.selectedPricePlanRecurrence === 'annual') {
      return this.intl.t('price_plan.company-creators.check-out.total-amount');
    } else if (this.selectedPricePlanRecurrence === 'monthly') {
      return this.intl.t('order-new.disclaimer.monthly', {
        name: this.capitalizedBrandName,
        amount: formatMoney({
          amount: this.regularPrice,
          token: 'money-symbol-compact',
        }),
      });
    }
  }

  collectPaymentTask = dropTask(
    waitFor(async () => {
      let organizationId = this.registration.belongsTo('organization').id();

      try {
        this.segment.track('onboarding_partner_payment_requested');

        let pricePlans = await this.checkout.fetchPricePlansForCompanyCreationTask.perform({
          legalCountry: this.model.selectedProducts.legal_country,
          partnerName: this.model.registration.partnerName?.toLowerCase(),
        });

        let selectedPricePlan = pricePlans.products.find(
          plan => plan.id === this.model.selectedPricePlan.product_id
        );

        await this.payments.collectInitialDeposit(this.card, {
          registration: this.registration,
          pricePlanId: selectedPricePlan.id,
        });

        this.segment.track('onboarding_partner_payment_confirmed');

        let recurrence = this.selectedPricePlanRecurrence;
        let revenue;
        let yearlyRevenue;

        let { tracking_details } = selectedPricePlan;

        revenue =
          recurrence === 'annual'
            ? tracking_details.annual_plan_conversion_value
            : tracking_details.monthly_plan_conversion_value;

        yearlyRevenue =
          recurrence === 'annual'
            ? tracking_details.annual_plan_yearly_revenue
            : tracking_details.monthly_plan_yearly_revenue;

        this.segment.track('onboarding_partner_contract_signed', {
          original_price_plan_code: this.model.selectedPricePlan.product_id,
          recurrence,
          revenue,
          k_deposit: 'true',
          ec_order_id: this.currentUser.user?.segmentId,
          ec_order_revenue: yearlyRevenue,
          currency: tracking_details.conversion_currency,
          ec_product: {
            product_id: selectedPricePlan.id,
            product_name: selectedPricePlan.code,
            product_brand: 'Qonto',
            product_category: 'Incorporated',
            product_category2: selectedPricePlan.lineup,
            product_price: yearlyRevenue,
            product_quantity: 1,
          },
          organization_id: organizationId,
          order_id: organizationId,
        });

        this.router.transitionTo('registration.almost-ready');
      } catch {
        this.segment.track('onboarding_partner_payment_failed');
        this.router.transitionTo('registration.payment-error');
      }
    })
  );

  @action
  onStripeCardChange(stripeElement, { complete }) {
    this.card = stripeElement;
    this.isComplete = complete;
  }

  @action
  updateTermsAndConditions() {
    this.termsAndConditionsChecked = !this.termsAndConditionsChecked;
  }
  styles = styles;
}
